/**
 * Sell-form identity policy (brand / model).
 *
 * Owner for how sellers attach brand/model to a listing:
 * - Brand is optional and catalog-only (search → select, or skip).
 * - Model is not collected on sell for now (avoids free-text title escape hatches).
 *
 * Catalog source: {@link getBrandSuggestions} in `brandSuggestions.ts`.
 * Title composition still accepts empty model via the shared title engine.
 */

import type { ItemCategory } from "@/lib/types";
import { getBrandSuggestions } from "@/lib/listing/brandSuggestions";

/** Flip when sell should collect model again (catalog-only; never free-text). */
export const SELL_LISTING_COLLECTS_MODEL = false;

export const CATALOG_BRAND_REQUIRED_MESSAGE =
  "Choose a brand from the list, or clear brand to skip";

export function isSellListingModelFieldEnabled(): boolean {
  return SELL_LISTING_COLLECTS_MODEL;
}

/** Whether category field config may show the model input on sell surfaces. */
export function sellListingShowsModelField(
  categoryAllowsModel: boolean,
): boolean {
  return isSellListingModelFieldEnabled() && categoryAllowsModel;
}

/**
 * Whether sell should show the brand field.
 * A registered empty subcategory list hides brand even when the category allows it.
 * Callers pass the category field-config flag; this module does not read field configs.
 */
export function sellListingShowsBrandField(
  category: ItemCategory | string | null | undefined,
  subcategoryId?: string | null,
  categoryAllowsBrand = true,
): boolean {
  if (!categoryAllowsBrand) return false;
  return getBrandSuggestions(category, subcategoryId).length > 0;
}

/**
 * Resolve a typed brand to the canonical catalog spelling, or null if empty /
 * not in the category or subcategory catalog.
 */
export function resolveCatalogBrand(
  brand: string | null | undefined,
  category: ItemCategory | string | null | undefined,
  subcategoryId?: string | null,
): string | null {
  const trimmed = brand?.trim() ?? "";
  if (!trimmed) return null;
  const catalog = getBrandSuggestions(category, subcategoryId);
  const match = catalog.find(
    (entry) => entry.toLowerCase() === trimmed.toLowerCase(),
  );
  return match ?? null;
}

export function isCatalogBrand(
  brand: string | null | undefined,
  category: ItemCategory | string | null | undefined,
  subcategoryId?: string | null,
): boolean {
  return resolveCatalogBrand(brand, category, subcategoryId) != null;
}

/** Empty is allowed; non-empty must match the resolved catalog. */
export function validateOptionalCatalogBrand(
  brand: string | null | undefined,
  category: ItemCategory | string | null | undefined,
  subcategoryId?: string | null,
): string | undefined {
  const trimmed = brand?.trim() ?? "";
  if (!trimmed) return undefined;
  if (!isCatalogBrand(trimmed, category, subcategoryId)) {
    return CATALOG_BRAND_REQUIRED_MESSAGE;
  }
  return undefined;
}

export type SellListingIdentityInput = {
  brand?: string | null;
  model?: string | null;
  category?: ItemCategory | string | null;
  /** When set, a subcategory catalog replaces the category list. */
  subcategoryId?: string | null;
  /** When set, an unchanged non-catalog brand may be kept (edit grandfather). */
  previousBrand?: string | null;
};

export type SellListingIdentityFields = {
  brand: string;
  model: string;
};

/**
 * Normalize identity for create/update persistence.
 * - Brand: empty, catalog match, or unchanged previousBrand (edit only).
 * - Model: cleared while {@link SELL_LISTING_COLLECTS_MODEL} is false.
 */
export function resolveSellListingIdentityFields(
  input: SellListingIdentityInput,
): SellListingIdentityFields {
  const trimmed = input.brand?.trim() ?? "";
  const model = isSellListingModelFieldEnabled()
    ? (input.model?.trim() ?? "")
    : "";

  if (!trimmed) {
    return { brand: "", model };
  }

  const catalog = resolveCatalogBrand(
    trimmed,
    input.category,
    input.subcategoryId,
  );
  if (catalog) {
    return { brand: catalog, model };
  }

  const previous = input.previousBrand?.trim() ?? "";
  if (previous && trimmed === previous) {
    return { brand: trimmed, model };
  }

  throw new Error(CATALOG_BRAND_REQUIRED_MESSAGE);
}
